import { makeAutoObservable, runInAction } from "mobx";
import { ServiceStore } from "@intrasync/stores";
import type { RootStore } from "@intrasync/stores";

import type {
  materials,
  materialsData,
} from "types/api-types/manufacturing/materials/materials.types";
import type {
  materialInventory,
  materialInventoryData,
  materialInventoryPatch,
} from "types/api-types/manufacturing/material-inventory/material-inventory.types";

interface InventoryAdjustment {
  inventoryRecordId: number; // ID of the material-inventory record being adjusted
  materialId: number;
  locationId: number;
  currentQuantity: number;
  adjustmentQuantity: number;
  adjustmentType: "increase" | "decrease" | "set";
  reason: string;
  notes: string;
  lotNumber?: string;
  batchNumber?: string;
  expirationDate?: string;
}

interface StockAlert {
  inventoryRecordId: number;
  materialId: number;
  locationId: number;
  currentQuantity: number;
  availableQuantity: number;
  reservedQuantity: number;
  lotNumber?: string;
  alertType: "low_stock" | "out_of_stock" | "expired" | "near_expiry";
  priority: "low" | "medium" | "high" | "critical";
}

interface RawMaterialInventoryData {
  locationId: string;
  adjustments: InventoryAdjustment[];
  cycleCounts: boolean;
  auditTrail: boolean;
  notifications: string[];
  notes: string;
  adjustedBy: string;
  adjustmentDate: string;
  status: "draft" | "pending_approval" | "approved" | "rejected";
}

class InventoryAdjustmentsWizardStore {
  rootStore: RootStore;

  // UI State
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Form Data
  formData: RawMaterialInventoryData = {
    locationId: "",
    adjustments: [],
    cycleCounts: false,
    auditTrail: true,
    notifications: [],
    notes: "",
    adjustedBy: "",
    adjustmentDate: new Date().toISOString().split("T")[0],
    status: "draft",
  };

  // Lookup Data
  stockAlerts: StockAlert[] = [];
  selectedLocationInventory: materialInventory[] = []; // Inventory records for selected location

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  };

  updateFormData = async <K extends keyof RawMaterialInventoryData>(
    field: K,
    value: RawMaterialInventoryData[K]
  ) => {
    this.formData[field] = value;

    // Load inventory for the selected location
    if (field === "locationId" && value) {
      await this.loadLocationInventory(value as string);
      // Clear existing adjustments when location changes
      this.formData.adjustments = [];
    }
  };

  addAdjustment = (adjustment: InventoryAdjustment) => {
    this.formData.adjustments.push(adjustment);
  };

  removeAdjustment = (index: number) => {
    this.formData.adjustments.splice(index, 1);
  };

  updateAdjustment = (index: number, adjustment: InventoryAdjustment) => {
    this.formData.adjustments[index] = adjustment;
  };

  addNotification = (userId: string) => {
    if (!this.formData.notifications.includes(userId)) {
      this.formData.notifications.push(userId);
    }
  };

  removeNotification = (userId: string) => {
    const index = this.formData.notifications.indexOf(userId);
    if (index > -1) {
      this.formData.notifications.splice(index, 1);
    }
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.error = null;

    switch (this.activeStep) {
      case 0:
        if (!this.formData.locationId) {
          this.error = "Please select a location";
          return false;
        }
        return true;

      case 1:
        if (this.formData.adjustments.length === 0) {
          this.error = "Please add at least one inventory adjustment";
          return false;
        }

        const invalidAdjustments = this.formData.adjustments.some(
          (adj) =>
            !adj.inventoryRecordId ||
            !adj.reason ||
            adj.adjustmentQuantity === 0
        );
        if (invalidAdjustments) {
          this.error = "Please complete all adjustment details";
          return false;
        }
        return true;

      case 2:
        // Stock alerts validation is optional
        return true;

      case 3:
        if (!this.formData.adjustedBy) {
          this.error = "Please specify who is making these adjustments";
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  // API Actions
  loadInitialData = async () => {
    this.loading = true;
    this.error = null;

    try {
      const materialsStore = (this.rootStore as any).materialsStore;
      const inventoryStore = (this.rootStore as any).materialInventoryStore;
      const locationsStore = (this.rootStore as any).materialLocationsStore;

      await Promise.all([
        materialsStore?.find(),
        inventoryStore?.find(),
        locationsStore?.find(),
      ]);

      runInAction(() => {
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load initial data";
        this.loading = false;
      });
    }
  };

  loadLocationInventory = async (locationId: string) => {
    const inventoryStore = (this.rootStore as any).materialInventoryStore;
    const locationIdNum = parseInt(locationId);

    // Make sure inventory data is loaded
    if (!inventoryStore?.entities || inventoryStore.entities.length === 0) {
      await inventoryStore?.find();
    }

    // Filter inventory records for the selected location
    this.selectedLocationInventory =
      inventoryStore?.entities.filter(
        (inv: materialInventory) => inv.locationId === locationIdNum
      ) || [];

    // Generate stock alerts for this location
    this.generateStockAlerts();
  };

  generateStockAlerts = () => {
    const alerts: StockAlert[] = [];
    const materialsStore = (this.rootStore as any).materialsStore;

    this.selectedLocationInventory.forEach((inventory) => {
      const material = materialsStore?.entities.find(
        (m: materials) => m.id === inventory.materialId
      );

      if (!material) return;

      const currentQuantity = inventory.quantity || 0;
      const availableQuantity =
        inventory.availableQuantity ||
        currentQuantity - (inventory.reservedQuantity || 0);
      const reservedQuantity = inventory.reservedQuantity || 0;

      // Check stock levels based on material's min order quantity
      const minStock = material.minOrderQuantity || 0;

      if (currentQuantity === 0) {
        alerts.push({
          inventoryRecordId: inventory.id,
          materialId: inventory.materialId,
          locationId: inventory.locationId,
          currentQuantity,
          availableQuantity,
          reservedQuantity,
          lotNumber: inventory.lotNumber,
          alertType: "out_of_stock",
          priority: "critical",
        });
      } else if (availableQuantity === 0 && reservedQuantity > 0) {
        alerts.push({
          inventoryRecordId: inventory.id,
          materialId: inventory.materialId,
          locationId: inventory.locationId,
          currentQuantity,
          availableQuantity,
          reservedQuantity,
          lotNumber: inventory.lotNumber,
          alertType: "low_stock",
          priority: "high",
        });
      } else if (currentQuantity <= minStock) {
        alerts.push({
          inventoryRecordId: inventory.id,
          materialId: inventory.materialId,
          locationId: inventory.locationId,
          currentQuantity,
          availableQuantity,
          reservedQuantity,
          lotNumber: inventory.lotNumber,
          alertType: "low_stock",
          priority: "medium",
        });
      }

      // Check for expiration dates if available
      if (inventory.expirationDate) {
        const expirationDate = new Date(inventory.expirationDate);
        const now = new Date();
        const daysUntilExpiry = Math.ceil(
          (expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        );

        if (daysUntilExpiry < 0) {
          alerts.push({
            inventoryRecordId: inventory.id,
            materialId: inventory.materialId,
            locationId: inventory.locationId,
            currentQuantity,
            availableQuantity,
            reservedQuantity,
            lotNumber: inventory.lotNumber,
            alertType: "expired",
            priority: "critical",
          });
        } else if (daysUntilExpiry <= 30) {
          alerts.push({
            inventoryRecordId: inventory.id,
            materialId: inventory.materialId,
            locationId: inventory.locationId,
            currentQuantity,
            availableQuantity,
            reservedQuantity,
            lotNumber: inventory.lotNumber,
            alertType: "near_expiry",
            priority: "medium",
          });
        }
      }
    });

    this.stockAlerts = alerts;
  };

  submitInventoryAdjustments = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      const inventoryStore = (this.rootStore as any).materialInventoryStore;

      // Process each adjustment
      for (const adjustment of this.formData.adjustments) {
        const inventoryRecord = this.selectedLocationInventory.find(
          (inv) => inv.id === adjustment.inventoryRecordId
        );

        if (!inventoryRecord) continue;

        let newQuantity: number;
        switch (adjustment.adjustmentType) {
          case "increase":
            newQuantity =
              inventoryRecord.quantity + adjustment.adjustmentQuantity;
            break;
          case "decrease":
            newQuantity = Math.max(
              0,
              inventoryRecord.quantity - adjustment.adjustmentQuantity
            );
            break;
          case "set":
            newQuantity = adjustment.adjustmentQuantity;
            break;
          default:
            newQuantity = inventoryRecord.quantity;
        }

        const availableQuantity =
          newQuantity - (inventoryRecord.reservedQuantity || 0);

        const patchData: materialInventoryPatch = {
          quantity: newQuantity,
          availableQuantity: availableQuantity,
          lastCountDate: new Date().toISOString(),
          lastCountBy: parseInt(this.formData.adjustedBy) || undefined,
          notes: adjustment.notes || inventoryRecord.notes || "", // Ensure notes is always a string
        };

        await inventoryStore?.patch(adjustment.inventoryRecordId, patchData);
      }

      runInAction(() => {
        this.success = "Inventory adjustments submitted successfully!";
        this.loading = false;

        // Reset form after successful submission
        setTimeout(() => {
          this.reset();
          this.rootStore.routerStore.goTo("Materials", { tab: "inventory" });
        }, 2000);
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to submit inventory adjustments";
        this.loading = false;
      });
    }
  };

  // Computed Values
  get users() {
    return (this.rootStore as any).userStore?.entities || [];
  }

  get locations() {
    const locationsStore = (this.rootStore as any).materialLocationsStore;
    return locationsStore?.entities || [];
  }

  get materials(): materials[] {
    const materialsStore = (this.rootStore as any).materialsStore;
    return materialsStore?.entities || [];
  }

  get materialInventory(): materialInventory[] {
    const inventoryStore = (this.rootStore as any).materialInventoryStore;
    return inventoryStore?.entities || [];
  }

  get selectedLocation() {
    return this.locations.find((l: any) => l.id === parseInt(this.formData.locationId));
  }

  get totalAdjustmentValue() {
    return this.formData.adjustments.reduce((sum, adj) => {
      const inventoryRecord = this.selectedLocationInventory.find(
        (inv) => inv.id === adj.inventoryRecordId
      );
      const cost = inventoryRecord?.cost || 0;
      return sum + Math.abs(adj.adjustmentQuantity) * cost;
    }, 0);
  }

  get adjustmentsByType() {
    const summary = {
      increase: this.formData.adjustments.filter(
        (adj) => adj.adjustmentType === "increase"
      ).length,
      decrease: this.formData.adjustments.filter(
        (adj) => adj.adjustmentType === "decrease"
      ).length,
      set: this.formData.adjustments.filter(
        (adj) => adj.adjustmentType === "set"
      ).length,
    };
    return summary;
  }

  get criticalAlerts() {
    return this.stockAlerts.filter((alert) => alert.priority === "critical");
  }

  get highPriorityAlerts() {
    return this.stockAlerts.filter((alert) => alert.priority === "high");
  }

  getMaterialById = (materialId: number): materials | undefined => {
    const materialsStore = (this.rootStore as any).materialsStore;
    return materialsStore?.entities.find((m: materials) => m.id === materialId);
  };

  getInventoryRecordById = (
    inventoryId: number
  ): materialInventory | undefined => {
    return this.selectedLocationInventory.find((inv) => inv.id === inventoryId);
  };

  // Reset
  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.formData = {
      locationId: "",
      adjustments: [],
      cycleCounts: false,
      auditTrail: true,
      notifications: [],
      notes: "",
      adjustedBy: "",
      adjustmentDate: new Date().toISOString().split("T")[0],
      status: "draft",
    };
    this.stockAlerts = [];
  };
}

export default InventoryAdjustmentsWizardStore;
